import React, { useState, useCallback } from 'react'
import { Handle, Position } from '@xyflow/react'
import { Device, Port, SignalType } from '@/types/devices'
import { SIGNAL_TYPE_COLORS, SIGNAL_TYPE_LABELS } from '@/constants/colors'
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  CategoryIcon,
} from '@/constants/categories'

interface CustomNodeData extends Device {
  onEdit?: (device: Device) => void
  onDelete?: (id: string) => void
  isSelected?: boolean
}

type PortSummary = { signalType: SignalType; count: number }[]

const getPortSummary = (ports: Port[]): PortSummary => {
  const counts: Partial<Record<SignalType, number>> = {}
  ports.forEach(p => {
    counts[p.signalType] = (counts[p.signalType] || 0) + 1
  })
  return Object.entries(counts).map(([st, count]) => ({
    signalType: st as SignalType,
    count: count!,
  }))
}

const CollapsedHandles: React.FC<{
  ports: Port[]
  isInput: boolean
}> = ({ ports, isInput }) => {
  if (ports.length === 0) return null

  const position = isInput ? Position.Left : Position.Right
  const handleType = isInput ? 'target' : 'source'

  // Space handles further apart so the port labels that appear on
  // device hover can read without overlapping. 10px gap is a
  // reasonable compromise between density and legibility.
  const spacing = Math.min(14, Math.max(10, 140 / Math.max(ports.length, 1)))
  const totalHeight = (ports.length - 1) * spacing
  const startOffset = -totalHeight / 2

  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        [isInput ? 'left' : 'right']: '-4px',
        height: 0,
        width: 0,
      }}
    >
      {ports.map((port, idx) => {
        const top = `${startOffset + idx * spacing}px`
        return (
          // Per-handle hover wrapper. Each port gets its own Tailwind
          // `group/port` scope so only the hovered handle's label
          // renders — fixes label overlap on dense devices like the
          // AudioFuse 16Rig. The wrapper is wider than the handle
          // itself so hover is easy to hit without missing the 10px
          // drag target.
          <div
            key={port.id}
            className="group/port absolute"
            style={{
              top,
              [isInput ? 'left' : 'right']: '-10px',
              width: '20px',
              height: `${Math.max(spacing, 14)}px`,
              transform: 'translateY(-50%)',
            }}
          >
            <Handle
              type={handleType}
              position={position}
              id={port.id}
              style={{
                position: 'absolute',
                top: '50%',
                [isInput ? 'left' : 'right']: '0px',
                width: '10px',
                height: '10px',
                background: SIGNAL_TYPE_COLORS[port.signalType],
                border: '1px solid rgba(0,0,0,0.3)',
                borderRadius: '50%',
                transform: 'translateY(-50%)',
              }}
            />
            <div
              className={`absolute whitespace-nowrap text-[10px] font-medium px-1.5 py-0.5 rounded-md shadow-lg pointer-events-none opacity-0 group-hover/port:opacity-100 transition-opacity duration-75 bg-gray-900/95 dark:bg-gray-800/98 text-white border border-gray-700/60 z-20 ${
                isInput ? 'right-6' : 'left-6'
              }`}
              style={{
                top: '50%',
                transform: 'translateY(-50%)',
                borderLeftWidth: !isInput ? 3 : undefined,
                borderRightWidth: isInput ? 3 : undefined,
                borderLeftColor: !isInput
                  ? SIGNAL_TYPE_COLORS[port.signalType]
                  : undefined,
                borderRightColor: isInput
                  ? SIGNAL_TYPE_COLORS[port.signalType]
                  : undefined,
              }}
            >
              {port.name}
              {port.bidirectional && (
                <span className="ml-1 text-[9px] opacity-70">⇄</span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const ExpandedPortGroup: React.FC<{
  signalType: SignalType
  ports: Port[]
  isInput: boolean
}> = ({ signalType, ports, isInput }) => {
  if (ports.length === 0) return null

  const color = SIGNAL_TYPE_COLORS[signalType]
  const label = SIGNAL_TYPE_LABELS[signalType]

  return (
    <div className="mt-1.5">
      <div
        className="text-[10px] font-semibold mb-0.5 opacity-80"
        style={{ color }}
      >
        {label}
      </div>
      {ports.map(port => (
        <div
          key={port.id}
          className={`flex items-center relative py-0.5 ${
            isInput ? 'flex-row' : 'flex-row-reverse'
          }`}
        >
          <Handle
            type={isInput ? 'target' : 'source'}
            position={isInput ? Position.Left : Position.Right}
            id={port.id}
            style={{
              background: color,
              width: '8px',
              height: '8px',
              border: '1px solid rgba(0,0,0,0.3)',
            }}
          />
          <div className={`flex flex-col ${isInput ? 'ml-3' : 'mr-3'}`}>
            <span className="text-[11px] text-gray-700 dark:text-gray-300 leading-tight">
              {port.name}
            </span>
            {port.connectorFormat && (
              <span className="text-[9px] text-gray-400 leading-tight">
                {port.connectorFormat}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

type GroupedPorts = Record<SignalType, Port[]>

const groupPortsBySignalType = (ports: Port[]): GroupedPorts => {
  const grouped: GroupedPorts = {
    audio: [],
    midi: [],
    sync: [],
    cv_gate: [],
    adat: [],
    word_clock: [],
    usb: [],
    ethernet: [],
  }
  ports.forEach(port => {
    if (port.signalType in grouped) {
      grouped[port.signalType].push(port)
    }
  })
  return grouped
}

const HoverDeleteButton: React.FC<{ onDelete: () => void }> = ({
  onDelete,
}) => (
  <button
    onClick={e => {
      e.stopPropagation()
      onDelete()
    }}
    className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-10"
    title="Delete device"
  >
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <path
        d="M5 2V1h6v1h4v2H1V2h4zM3 6h10l-.8 9H3.8L3 6zm3 1.5v5.5h1v-5.5H6zm3 0v5.5h1v-5.5H9z"
        fill="currentColor"
      />
    </svg>
  </button>
)

const DeviceNode: React.FC<{ data: CustomNodeData }> = ({ data }) => {
  const [expanded, setExpanded] = useState(false)
  // Drilldown is handled by node-level filtering in EditorClient now, so
  // every device that renders is fully opaque — no dimming to apply.

  const categoryColor = CATEGORY_COLORS[data.category]
  const categoryLabel = CATEGORY_LABELS[data.category]

  const inputSummary = getPortSummary(data.inputs)
  const outputSummary = getPortSummary(data.outputs)
  const inputGroups = groupPortsBySignalType(data.inputs)
  const outputGroups = groupPortsBySignalType(data.outputs)

  const toggleExpand = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return
    setExpanded(prev => !prev)
  }, [])

  if (!expanded) {
    return (
      <div
        onClick={toggleExpand}
        className="group relative cursor-pointer select-none"
        style={{ minWidth: '160px' }}
      >
        {data.onDelete && (
          <HoverDeleteButton onDelete={() => data.onDelete!(data.id)} />
        )}
        <div
          className={`px-3 py-2.5 rounded-lg bg-white dark:bg-gray-800 shadow-lg border dark:border-gray-700 ${
            data.isSelected ? 'ring-2 ring-accent' : ''
          }`}
          style={{
            borderLeft: `4px solid ${categoryColor}`,
          }}
        >
          <div className="flex items-center gap-2.5">
            <div className="flex-shrink-0 opacity-80">
              <CategoryIcon category={data.category} size={28} />
            </div>
            <div className="flex-1 min-w-0">
              {data.manufacturer && (
                <div className="text-[9px] text-gray-400 uppercase tracking-wider leading-tight truncate">
                  {data.manufacturer}
                </div>
              )}
              <div className="text-xs font-bold text-gray-900 dark:text-white leading-tight truncate">
                {data.name}
              </div>
              <div
                className="text-[9px] font-medium mt-0.5"
                style={{ color: categoryColor }}
              >
                {categoryLabel}
              </div>
            </div>
          </div>

          <div className="flex gap-1 mt-2 flex-wrap">
            {inputSummary.length > 0 && (
              <div className="flex gap-0.5 items-center">
                <span className="text-[8px] text-gray-400 mr-0.5">IN:</span>
                {inputSummary.map(({ signalType, count }) => (
                  <span
                    key={signalType}
                    className="text-[8px] px-1 py-0 rounded-sm text-white font-medium leading-relaxed"
                    style={{
                      backgroundColor: SIGNAL_TYPE_COLORS[signalType],
                    }}
                  >
                    {count} {SIGNAL_TYPE_LABELS[signalType]}
                  </span>
                ))}
              </div>
            )}
            {outputSummary.length > 0 && (
              <div className="flex gap-0.5 items-center">
                <span className="text-[8px] text-gray-400 mr-0.5">OUT:</span>
                {outputSummary.map(({ signalType, count }) => (
                  <span
                    key={signalType}
                    className="text-[8px] px-1 py-0 rounded-sm text-white font-medium leading-relaxed"
                    style={{
                      backgroundColor: SIGNAL_TYPE_COLORS[signalType],
                    }}
                  >
                    {count} {SIGNAL_TYPE_LABELS[signalType]}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <CollapsedHandles ports={data.inputs} isInput={true} />
        <CollapsedHandles ports={data.outputs} isInput={false} />
      </div>
    )
  }

  return (
    <div
      onClick={toggleExpand}
      className="group cursor-pointer select-none relative"
      style={{ minWidth: '220px', maxWidth: '300px' }}
    >
      {data.onDelete && (
        <HoverDeleteButton onDelete={() => data.onDelete!(data.id)} />
      )}
      <div
        className={`px-4 py-3 rounded-lg bg-white dark:bg-gray-800 shadow-lg border dark:border-gray-700 ${
          data.isSelected ? 'ring-2 ring-accent' : ''
        }`}
        style={{ borderLeft: `4px solid ${categoryColor}` }}
      >
        <div className="flex items-center gap-2.5 mb-2">
          <div className="flex-shrink-0 opacity-80">
            <CategoryIcon category={data.category} size={24} />
          </div>
          <div className="flex-1 min-w-0">
            {data.manufacturer && (
              <div className="text-[9px] text-gray-400 uppercase tracking-wider leading-tight">
                {data.manufacturer}
              </div>
            )}
            <div className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
              {data.name}
            </div>
          </div>
        </div>

        {data.inputs.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-1.5 mt-1.5">
            <div className="text-[9px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Inputs
            </div>
            {Object.entries(inputGroups).map(([signalType, ports]) => (
              <ExpandedPortGroup
                key={signalType}
                signalType={signalType as SignalType}
                ports={ports}
                isInput={true}
              />
            ))}
          </div>
        )}

        {data.outputs.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-1.5 mt-1.5">
            <div className="text-[9px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Outputs
            </div>
            {Object.entries(outputGroups).map(([signalType, ports]) => (
              <ExpandedPortGroup
                key={signalType}
                signalType={signalType as SignalType}
                ports={ports}
                isInput={false}
              />
            ))}
          </div>
        )}

        <div className="mt-2 flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700 pt-2">
          {data.onEdit && (
            <button
              className="px-2 py-1 bg-accent text-[color:var(--accent-ink)] rounded text-[10px] hover:bg-[color:var(--accent-hover)] transition-colors"
              onClick={e => {
                e.stopPropagation()
                data.onEdit!(data)
              }}
            >
              Edit
            </button>
          )}
          {data.onDelete && (
            <button
              className="px-2 py-1 bg-red-500 text-white rounded text-[10px] hover:bg-red-600 transition-colors"
              onClick={e => {
                e.stopPropagation()
                data.onDelete!(data.id)
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default DeviceNode
